import type { TimelinePoint } from './timeline';

export const REQUESTS_RESOURCE = 'requests.requests';
export const REQUESTS_KEY = ['requests'] as const;
/**
 * „Do wysłania” over „Dostępność → Ludzie” (owner 2026-10-27 12:41): under the crew's key, so `refreshCrew` reads it
 * again too; every send to people refreshes it (review I2).
 */
export const UNSENT_KEY = ['crew', 'people-availability', 'unsent'] as const;

export const ANSWERS = { moge: 'Mogę', nie_moge: 'Nie mogę', pytanie: 'Mam pytanie', czesciowo: 'Część dni' } as const;
export type Answer = keyof typeof ANSWERS;
/** The badge beside a person: short, lower case. */
export const BADGE_LABELS: Record<Answer, string> = { moge: 'mogę', nie_moge: 'nie mogę', pytanie: 'pytanie', czesciowo: 'część dni' };
/**
 * Owner 2026-10-30 01:30: a day's answer a newer mark of the person's day changed — `answer`: what was answered; the mark
 * (`state`, `hours`), how (`source`), who (`by`) and when (`at`) — each only when the role may see it (rule 7a).
 */
export type DayChange = { answer: boolean; state?: 'available' | 'unavailable'; hours?: string; source?: 'manual' | 'answer' | 'self'; by?: string; at?: string };
/**
 * Stage 2: one asked day with its answer as it stands (absent: the role may not see answers); `stale`: no longer a day
 * of the event; `changed`: a newer mark changed it (owner 2026-10-30 01:30).
 */
export type DayAnswer = { day: string; answer?: boolean | null; stale?: boolean; changed?: DayChange };

/** One person asked about an event, as the API sends it: fields the role may not see are absent (rule 7a). */
export type Recipient = {
  id: string;
  personId: string;
  personName: string | null;
  message?: string;
  channels?: string[];
  sentAt?: string;
  answer?: Answer | null;
  answerNote?: string;
  answeredAt?: string | null;
  /** Owner 2026-10-30 01:30: what was answered, when a newer mark made `answer` say otherwise. */
  givenAnswer?: Answer;
  /** Stage 2: 'event' — the crew's question; 'dates' — chosen days (the grid, „Poproś o dostępność”). */
  kind?: 'event' | 'dates';
  days?: DayAnswer[];
  /** The event's dates changed after the question: „nieaktualne — zapytaj o nową datę”. */
  stale?: boolean;
  /** „tak” for every day added the person to the crew (or found them there); `addRefused`: why not. */
  added?: boolean;
  addRefused?: string;
  /** Owner 2026-10-29 00:32: „Zrezygnuj z przydziału” — nobody waits to place the person by hand any more. */
  assignDismissed?: true;
  /** Owner 2026-10-30 21:31: the correspondence, oldest first (only with the field „Odpowiedź”). */
  timeline?: TimelinePoint[];
  /** The newest point (the decision that may still change). */
  latestPointId?: string;
  /** Owner 2026-10-30 21:49: still in the lists (else only in „Historia”). */
  active?: boolean;
  /** The event was deleted (only in „Historia”: „wydarzenie usunięte”). */
  eventDeleted?: true;
};

/** One send of „Wyślij potwierdzenie do obsady”. */
export type ConfirmationSent = { id: string; eventId?: string; eventName?: string; sentAt?: string; sentBy: string; people: number };
/** The people centre's list (stage 2). */
export type QuestionsList = { questions: Array<Recipient & { eventId?: string; eventName?: string; superseded?: boolean }>; confirmations: ConfirmationSent[] };

/** Filtered by the user's rules: `name` only with the right to see people, `channels`/`sentAt` when visible. */
export type SendResult = {
  sent: { personId: string; name?: string; channels?: string[] }[];
  skipped: { personId: string; name?: string; sentAt?: string }[];
  noContact: { personId: string; name?: string }[];
};

/** What the public answer page gets for its token. */
export type AnswerPageData = {
  /** Stage 2: 'dates' — a question about days (maybe without an event); 'event' — the crew's (also „Mam pytanie”). */
  kind?: 'event' | 'dates';
  /** `segments`: several terms (owner 2026-10-21 00:44), as the message lists them; absent for one term. Null: days only. */
  event: { name: string; startsAt: string; endsAt: string; segments?: Array<{ startsAt: string; endsAt: string }>; location: string; locationAddress: string } | null;
  message: string;
  /** Stage 2: the days asked, each answered „tak” / „nie”. */
  days?: DayAnswer[];
  answer: Answer | null;
  answerNote: string;
  answeredAt: string | null;
  canAnswer: boolean;
  closed: boolean;
  stale?: boolean;
  superseded?: boolean;
  /** Owner 2026-10-30 21:31: the points of the question's correspondence (no who or how on this page). */
  history?: TimelinePoint[];
  /** The newest point the page shows: sent back with the answer (`basedOn`). */
  latestPointId?: string;
};

/** Owner 2026-10-28 12:57: one question on the page of all the person's open questions (its id, never its link). */
export type AnswerQuestion = AnswerPageData & { id: string };

const WEEKDAY = new Intl.DateTimeFormat('pl-PL', { weekday: 'long', timeZone: 'UTC' });
/** '2030-11-07' → 'czwartek 7.11.2030'. */
export const dayLabel = (day: string) => `${WEEKDAY.format(new Date(`${day}T12:00:00Z`))} ${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}.${day.slice(0, 4)}`;
/** '2030-11-07' → '7.11'. */
export const dayShortText = (day: string) => `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}`;
const CHANGED_AT = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
/** An instant as „30.10 01:35” (Warsaw). */
const changedAt = (iso: string) => {
  const p = Object.fromEntries(CHANGED_AT.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.day}.${p.month} ${p.hour}:${p.minute}`;
};
/**
 * Owner 2026-10-30 01:30: why a day's answer is not what was answered — „zmieniono 30.10 01:35 — sam pracownik” (by hand:
 * „ręcznie: Szef”; another answer: „odpowiedź na inne zapytanie”); some hours first („niedostępny 18:00–22:00; …”).
 * `mine` („Mój kalendarz”): the person's own change is „Ty”. What the role may not see is not said.
 */
export function changeNote(c: DayChange, mine = false): string {
  const who = c.source === 'self' ? (mine ? 'Ty' : 'sam pracownik') : c.source === 'manual' ? (c.by ? `ręcznie: ${c.by}` : 'ręcznie') : c.source === 'answer' ? 'odpowiedź na inne zapytanie' : '';
  const hours = c.hours && c.state ? `${c.state === 'available' ? 'dostępny' : 'niedostępny'} ${c.hours}; ` : '';
  return `${hours}zmieniono${c.at ? ` ${changedAt(c.at)}` : ''}${who ? ` — ${who}` : ''}`;
}
/** The answers per day in one line: „7.11 tak · 8.11 nie (zmieniono 30.10 01:35 — sam pracownik)”. */
export const daysAnswerText = (days: readonly DayAnswer[]) =>
  days.map((d) => `${dayShortText(d.day)}${d.answer === true ? ' tak' : d.answer === false ? ' nie' : ''}${d.changed ? ` (${changeNote(d.changed)})` : ''}${d.stale ? ' (nieaktualne)' : ''}`).join(' · ');

const SHORT = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
/** "20.10.2026, 14:05" in Warsaw time. */
export const shortDateTime = (iso: string) => SHORT.format(new Date(iso));

/** People of the list in their first order, each once. */
export function uniquePeople(list: { personId?: string; personName?: string }[]): { personId: string; personName: string }[] {
  const seen = new Map<string, string>();
  for (const a of list) if (a.personId && !seen.has(a.personId)) seen.set(a.personId, a.personName ?? 'Osoba');
  return [...seen].map(([personId, personName]) => ({ personId, personName }));
}
